import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGetAnnotatorsQuery } from '../../../../features/create-project-wizard/api/createProjectApiSlice';
import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { useGetProjectDetailsQuery } from '../../api/adminProjectsApiSlice';
import { addAnnotator, removeAnnotator, setAnnotators } from '../../state/adminProjectSlice';
import { selectAnnotators } from '../../state/selector';
import { User, Annotator } from '../../types/response';

interface UseAnnotatorsSelectionResults {
  annotators: User[];
  selectedAnnotators: Annotator[];
  handleAddAnnotator: (annotator: Annotator) => void;
  handleRemoveAnnotator: (annotator: Annotator) => void;
  annotatorInputValue: string;
  lastTypedAnnotatorValue: React.MutableRefObject<string>;
  setAnnotatorInputValue: (value: string) => void;
  isAnnotatorsLoading: boolean;
}

export const useAnnotatorsSelection = (): UseAnnotatorsSelectionResults => {
  const {
    data: annotatorsData,
    isLoading: isAnnotatorsLoading,
    isFetching: isAnnotatorsFetching,
  } = useGetAnnotatorsQuery();
  const allAnnotators: User[] = useMemo(() => annotatorsData?.data ?? [], [annotatorsData]);

  const { projectId } = useParams();
  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery({ projectId: projectId as string });

  const projectAnnotators: Annotator[] = useMemo(
    () => projectDetailsData?.data?.annotators ?? [],
    [projectDetailsData]
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setAnnotators(projectAnnotators));
  }, [projectAnnotators]);

  const selectedAnnotators = useAppSelector(selectAnnotators);

  const handleAddAnnotator = (annotator: Annotator) => {
    dispatch(addAnnotator(annotator));
  };
  const handleRemoveAnnotator = (annotator: Annotator) => {
    dispatch(removeAnnotator(annotator.id));
  };
  const [annotatorInputValue, setAnnotatorInputValue] = useState<string>('');

  const lastTypedAnnotatorValue = useRef<string>('');
  return {
    selectedAnnotators: selectedAnnotators as Annotator[],
    handleRemoveAnnotator,
    annotators: allAnnotators,
    annotatorInputValue,
    lastTypedAnnotatorValue,
    setAnnotatorInputValue,
    handleAddAnnotator,
    isAnnotatorsLoading: isAnnotatorsLoading || isAnnotatorsFetching,
  };
};
